import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '@axmit/error-helper';
import { AppDispatch } from 'app/store';
import {
  ITrackStoreAllStoresCollection,
  ITrackStoreLiveStoresCollection,
  ITrackStoreNotificationCollection,
  ITrackStoreNotificationCollectionFilters,
  ITrackStoreOnDutyCollection,
  ITrackStoreOnDutyCollectionFilters,
  ITrackStoreOrderDetails,
  ITrackStoreOrderDetailsFilters,
  ITrackStoreStoreCollection,
  ITrackStoreSummary,
  ITrackStoreSummaryFilters,
  ITrackStoreWeeklyGrid,
  ITrackStoreWeeklyGridFilters,
} from 'entities/TrackStore/TrackStore.models';
import { trackStoreTransport } from 'entities/TrackStore/TrackStore.transport';

export interface IState {
  // track store store collection
  trackStoreStoreCollectionLoading: boolean;
  trackStoreStoreCollection: ITrackStoreStoreCollection | null;
  trackStoreStoreCollectionError: IError | null;
  // track store summary
  trackStoreSummaryLoading: boolean;
  trackStoreSummary: ITrackStoreSummary | null;
  trackStoreSummaryError: IError | null;
  // track store weekly grid
  trackStoreWeeklyGridLoading: boolean;
  trackStoreWeeklyGrid: ITrackStoreWeeklyGrid | null;
  trackStoreWeeklyGridError: IError | null;
  // track store notification collection
  trackStoreNotificationCollectionLoading: boolean;
  trackStoreNotificationCollection: ITrackStoreNotificationCollection | null;
  trackStoreNotificationCollectionError: IError | null;
  // track store all stores collection
  trackStoreAllStoresCollectionLoading: boolean;
  trackStoreAllStoresCollection: ITrackStoreAllStoresCollection | null;
  trackStoreAllStoresCollectionError: IError | null;
  // track store on duty collection
  trackStoreOnDutyCollectionLoading: boolean;
  trackStoreOnDutyCollection: ITrackStoreOnDutyCollection | null;
  trackStoreOnDutyCollectionError: IError | null;
  // track store live stores collection
  trackStoreLiveStoresCollectionLoading: boolean;
  trackStoreLiveStoresCollection: ITrackStoreLiveStoresCollection | null;
  trackStoreLiveStoresCollectionError: IError | null;
  // track store order details
  trackStoreOrderDetailsLoading: boolean;
  trackStoreOrderDetails: ITrackStoreOrderDetails | null;
  trackStoreOrderDetailsError: IError | null;
}

const initialState: IState = {
  // store collection
  trackStoreStoreCollectionLoading: false,
  trackStoreStoreCollection: null,
  trackStoreStoreCollectionError: null,
  // summary
  trackStoreSummaryLoading: false,
  trackStoreSummary: null,
  trackStoreSummaryError: null,
  // weekly grid
  trackStoreWeeklyGridLoading: false,
  trackStoreWeeklyGrid: null,
  trackStoreWeeklyGridError: null,
  // notification collection
  trackStoreNotificationCollectionLoading: false,
  trackStoreNotificationCollection: null,
  trackStoreNotificationCollectionError: null,
  // all stores collection
  trackStoreAllStoresCollectionLoading: false,
  trackStoreAllStoresCollection: null,
  trackStoreAllStoresCollectionError: null,
  // on duty collection
  trackStoreOnDutyCollectionLoading: false,
  trackStoreOnDutyCollection: null,
  trackStoreOnDutyCollectionError: null,
  // live stores collection
  trackStoreLiveStoresCollectionLoading: false,
  trackStoreLiveStoresCollection: null,
  trackStoreLiveStoresCollectionError: null,
  // order details
  trackStoreOrderDetailsLoading: false,
  trackStoreOrderDetails: null,
  trackStoreOrderDetailsError: null,
};

export const purchaseOrderSlice = createSlice({
  name: 'trackStore',
  initialState,
  reducers: {
    // store collection
    setTrackStoreStoreCollectionLoading(state, action: PayloadAction<boolean>) {
      state.trackStoreStoreCollectionLoading = action.payload;
    },
    setTrackStoreStoreCollection(state, action: PayloadAction<ITrackStoreStoreCollection | null>) {
      state.trackStoreStoreCollection = action.payload;
    },
    setTrackStoreStoreCollectionError(state, action: PayloadAction<IError | null>) {
      state.trackStoreStoreCollectionError = action.payload;
    },
    // summary
    setTrackStoreSummaryLoading(state, action: PayloadAction<boolean>) {
      state.trackStoreSummaryLoading = action.payload;
    },
    setTrackStoreSummary(state, action: PayloadAction<ITrackStoreSummary | null>) {
      state.trackStoreSummary = action.payload;
    },
    setTrackStoreSummaryError(state, action: PayloadAction<IError | null>) {
      state.trackStoreSummaryError = action.payload;
    },
    // weekly grid
    setTrackStoreWeeklyGridLoading(state, action: PayloadAction<boolean>) {
      state.trackStoreWeeklyGridLoading = action.payload;
    },
    setTrackStoreWeeklyGrid(state, action: PayloadAction<ITrackStoreWeeklyGrid | null>) {
      state.trackStoreWeeklyGrid = action.payload;
    },
    setTrackStoreWeeklyGridError(state, action: PayloadAction<IError | null>) {
      state.trackStoreWeeklyGridError = action.payload;
    },
    // notification collection
    setTrackStoreNotificationCollectionLoading(state, action: PayloadAction<boolean>) {
      state.trackStoreNotificationCollectionLoading = action.payload;
    },
    setTrackStoreNotificationCollection(state, action: PayloadAction<ITrackStoreNotificationCollection | null>) {
      state.trackStoreNotificationCollection = action.payload;
    },
    setTrackStoreNotificationCollectionError(state, action: PayloadAction<IError | null>) {
      state.trackStoreNotificationCollectionError = action.payload;
    },
    // all stores collection
    setTrackStoreAllStoresCollectionLoading(state, action: PayloadAction<boolean>) {
      state.trackStoreAllStoresCollectionLoading = action.payload;
    },
    setTrackStoreAllStoresCollection(state, action: PayloadAction<ITrackStoreAllStoresCollection | null>) {
      state.trackStoreAllStoresCollection = action.payload;
    },
    setTrackStoreAllStoresCollectionError(state, action: PayloadAction<IError | null>) {
      state.trackStoreAllStoresCollectionError = action.payload;
    },
    // on duty collection
    setTrackStoreOnDutyCollectionLoading(state, action: PayloadAction<boolean>) {
      state.trackStoreOnDutyCollectionLoading = action.payload;
    },
    setTrackStoreOnDutyCollection(state, action: PayloadAction<ITrackStoreOnDutyCollection | null>) {
      state.trackStoreOnDutyCollection = action.payload;
    },
    setTrackStoreOnDutyCollectionError(state, action: PayloadAction<IError | null>) {
      state.trackStoreOnDutyCollectionError = action.payload;
    },
    // live stores collection
    setTrackStoreLiveStoresCollectionLoading(state, action: PayloadAction<boolean>) {
      state.trackStoreLiveStoresCollectionLoading = action.payload;
    },
    setTrackStoreLiveStoresCollection(state, action: PayloadAction<ITrackStoreLiveStoresCollection | null>) {
      state.trackStoreLiveStoresCollection = action.payload;
    },
    setTrackStoreLiveStoresCollectionError(state, action: PayloadAction<IError | null>) {
      state.trackStoreLiveStoresCollectionError = action.payload;
    },
    // order details
    setTrackStoreOrderDetailsLoading(state, action: PayloadAction<boolean>) {
      state.trackStoreOrderDetailsLoading = action.payload;
    },
    setTrackStoreOrderDetails(state, action: PayloadAction<ITrackStoreOrderDetails | null>) {
      state.trackStoreOrderDetails = action.payload;
    },
    setTrackStoreOrderDetailsError(state, action: PayloadAction<IError | null>) {
      state.trackStoreOrderDetailsError = action.payload;
    },
  },
});

export const {
  // store collection
  setTrackStoreStoreCollectionLoading,
  setTrackStoreStoreCollection,
  setTrackStoreStoreCollectionError,
  // summary
  setTrackStoreSummaryLoading,
  setTrackStoreSummary,
  setTrackStoreSummaryError,
  // weekly grid
  setTrackStoreWeeklyGridLoading,
  setTrackStoreWeeklyGrid,
  setTrackStoreWeeklyGridError,
  // notification collection
  setTrackStoreNotificationCollectionLoading,
  setTrackStoreNotificationCollection,
  setTrackStoreNotificationCollectionError,
  // all stores collection
  setTrackStoreAllStoresCollectionLoading,
  setTrackStoreAllStoresCollection,
  setTrackStoreAllStoresCollectionError,
  // on duty collection
  setTrackStoreOnDutyCollectionLoading,
  setTrackStoreOnDutyCollection,
  setTrackStoreOnDutyCollectionError,
  // live stores collection
  setTrackStoreLiveStoresCollectionLoading,
  setTrackStoreLiveStoresCollection,
  setTrackStoreLiveStoresCollectionError,
  // order details
  setTrackStoreOrderDetailsLoading,
  setTrackStoreOrderDetails,
  setTrackStoreOrderDetailsError,
} = purchaseOrderSlice.actions;
export default purchaseOrderSlice.reducer;

export const getTrackStoreStoreCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setTrackStoreStoreCollectionLoading(true));

    try {
      const trackStoreStoreCollection = await trackStoreTransport.getStoreCollection();

      dispatch(setTrackStoreStoreCollection(trackStoreStoreCollection));
    } catch (error) {
      const _error = error as IError;

      dispatch(setTrackStoreStoreCollectionError(_error));
    } finally {
      dispatch(setTrackStoreStoreCollectionLoading(false));
    }
  }

  return thunk;
};

export const getTrackStoreSummary = (filters: ITrackStoreSummaryFilters) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setTrackStoreSummaryLoading(true));

    try {
      const trackStoreSummary = await trackStoreTransport.getSummary(filters);

      dispatch(setTrackStoreSummary(trackStoreSummary));
    } catch (error) {
      const _error = error as IError;

      dispatch(setTrackStoreSummaryError(_error));
    } finally {
      dispatch(setTrackStoreSummaryLoading(false));
    }
  }

  return thunk;
};

export const getTrackStoreWeeklyGrid = (filters: ITrackStoreWeeklyGridFilters) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setTrackStoreWeeklyGridLoading(true));

    try {
      const trackStoreWeeklyGrid = await trackStoreTransport.getWeeklyGrid(filters);

      dispatch(setTrackStoreWeeklyGrid(trackStoreWeeklyGrid));
    } catch (error) {
      const _error = error as IError;

      dispatch(setTrackStoreWeeklyGridError(_error));
    } finally {
      dispatch(setTrackStoreWeeklyGridLoading(false));
    }
  }

  return thunk;
};

export const getTrackStoreNotificationCollection = (filters: ITrackStoreNotificationCollectionFilters) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setTrackStoreNotificationCollectionLoading(true));

    try {
      const trackStoreNotificationCollection = await trackStoreTransport.getNotificationCollection(filters);

      dispatch(setTrackStoreNotificationCollection(trackStoreNotificationCollection));
    } catch (error) {
      const _error = error as IError;

      dispatch(setTrackStoreNotificationCollectionError(_error));
    } finally {
      dispatch(setTrackStoreNotificationCollectionLoading(false));
    }
  }

  return thunk;
};

export const getTrackStoreAllStoresCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setTrackStoreAllStoresCollectionLoading(true));

    try {
      const trackStoreAllStoresCollection = await trackStoreTransport.getAllStoresCollection();

      dispatch(setTrackStoreAllStoresCollection(trackStoreAllStoresCollection));
    } catch (error) {
      const _error = error as IError;

      dispatch(setTrackStoreAllStoresCollectionError(_error));
    } finally {
      dispatch(setTrackStoreAllStoresCollectionLoading(false));
    }
  }

  return thunk;
};

export const getTrackStoreOnDutyCollection = (filters: ITrackStoreOnDutyCollectionFilters) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setTrackStoreOnDutyCollectionLoading(true));

    try {
      const trackStoreOnDutyCollection = await trackStoreTransport.getOnDutyCollection(filters);

      dispatch(setTrackStoreOnDutyCollection(trackStoreOnDutyCollection));
    } catch (error) {
      const _error = error as IError;

      dispatch(setTrackStoreOnDutyCollectionError(_error));
    } finally {
      dispatch(setTrackStoreOnDutyCollectionLoading(false));
    }
  }

  return thunk;
};

export const getTrackStoreLiveStoresCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setTrackStoreLiveStoresCollectionLoading(true));

    try {
      const trackStoreLiveStoresCollection = await trackStoreTransport.getLiveStoresCollection();

      dispatch(setTrackStoreLiveStoresCollection(trackStoreLiveStoresCollection));
    } catch (error) {
      const _error = error as IError;

      dispatch(setTrackStoreLiveStoresCollectionError(_error));
    } finally {
      dispatch(setTrackStoreLiveStoresCollectionLoading(false));
    }
  }

  return thunk;
};

export const getTrackStoreOrderDetails = (filters: ITrackStoreOrderDetailsFilters) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setTrackStoreOrderDetailsLoading(true));

    try {
      const trackStoreOrderDetails = await trackStoreTransport.getOrderDetails(filters);

      dispatch(setTrackStoreOrderDetails(trackStoreOrderDetails));
    } catch (error) {
      const _error = error as IError;

      dispatch(setTrackStoreOrderDetailsError(_error));
    } finally {
      dispatch(setTrackStoreOrderDetailsLoading(false));
    }
  }

  return thunk;
};
